import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import Accordian from "../../../../../components/Accordian";
import TextArea from "../../../../../components/TextArea";
import Input from "../../../../../components/Input";
import { CLEANING_MARGIN_PERCENTAGE, VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import ManagerStatementService from "../../../../../services/managerStatement.service";

interface CleaningSectionProps {
  statementId: string;
  cleaningDetails: string | null | undefined;
  cleaningSplitAmount: number | null | undefined;
  period?: string;
  readOnly?: boolean;
  onTotalsChange?: (cleaningSplitAmount: number) => void;
}

export interface CleaningSectionHandle {
  save: () => Promise<void>;
  isDirty: () => boolean;
}

const CleaningSection = forwardRef<CleaningSectionHandle, CleaningSectionProps>(({
  statementId, cleaningDetails, cleaningSplitAmount, period, readOnly, onTotalsChange,
}, ref) => {
  const { t } = useTranslation();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.cleaning';

  const defaultDetails = period ? t(`${base}.detailsDefaultText`, { period }) : '';

  const [expanded, setExpanded] = useState(true);
  const [details, setDetails] = useState(cleaningDetails || defaultDetails);
  const [amount, setAmount] = useState(cleaningSplitAmount !== null && cleaningSplitAmount !== undefined ? String(cleaningSplitAmount) : '');

  useEffect(() => {
    setDetails(cleaningDetails || defaultDetails);
    setAmount(cleaningSplitAmount !== null && cleaningSplitAmount !== undefined ? String(cleaningSplitAmount) : '');
  }, [cleaningDetails, cleaningSplitAmount, defaultDetails]);

  useEffect(() => {
    onTotalsChange?.((Number(amount) || 0) * (CLEANING_MARGIN_PERCENTAGE / 100));
  }, [amount, onTotalsChange]);

  const handleDetailsChange = useCallback((e: any) => {
    setDetails(e.target.value);
  }, []);

  const handleAmountChange = useCallback((e: any) => {
    setAmount(e.target.value);
  }, []);

  const save = useCallback(() => {
    return ManagerStatementService.saveCleaning(statementId, {
      cleaning_details: details.trim() === '' ? null : details,
      cleaning_split_amount: amount.trim() === '' ? null : Number(amount),
    }).then(() => undefined);
  }, [statementId, details, amount]);

  // The baseline is the last value the parent statement reported for these fields (props),
  // not a separately tracked ref - CleaningSection already re-syncs `details`/`amount` from
  // props via the effect above whenever a save completes and the parent reloads.
  const isDirty = useCallback(() => {
    const baselineDetails = cleaningDetails || defaultDetails;
    const baselineAmount = cleaningSplitAmount !== null && cleaningSplitAmount !== undefined ? String(cleaningSplitAmount) : '';
    return details !== baselineDetails || amount !== baselineAmount;
  }, [details, amount, cleaningDetails, cleaningSplitAmount, defaultDetails]);

  useImperativeHandle(ref, () => ({ save, isDirty }), [save, isDirty]);

  const summary = (
    <>
      <i className="ri-brush-line ri-22px me-2"></i>
      <h6 className="mb-0">{t(`${base}.title`)}</h6>
    </>
  );

  const content = (
    <div className="row g-6">
      <div className="col-md-9">
        <div className="form-floating form-floating-outline">
          <TextArea
            id={VALIDATION_FIELD_MAPPING.cleaningDetails}
            name={VALIDATION_FIELD_MAPPING.cleaningDetails}
            label={t(`${base}.detailsLabel`)}
            placeholder={t(`${base}.detailsPlaceholder`)}
            rows={2}
            className="form-control-lg h-100"
            value={details}
            disabled={readOnly}
            onChange={handleDetailsChange}
          />
        </div>
      </div>

      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Input
            type="number"
            id={VALIDATION_FIELD_MAPPING.cleaningSplitAmount}
            name={VALIDATION_FIELD_MAPPING.cleaningSplitAmount}
            label={t(`${base}.splitAmountLabel`)}
            placeholder={t(`${base}.splitAmountPlaceholder`)}
            value={amount}
            disabled={readOnly}
            onChange={handleAmountChange}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Accordian
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      summary={summary}
      content={content}
    />
  );
});

CleaningSection.displayName = 'CleaningSection';

export default CleaningSection;
